import { useState, useEffect, useCallback } from 'react';
import { Event } from '../types/types';
import { eventService } from '../services/eventService';
import { marketService } from '../services/marketService';
import { getStartTimeForEvents } from '../utils/eventUtils';

interface UseEventsResult {
  events: Event[];
  isLoading: boolean;
  error: Error | null;
  refreshEvents: () => Promise<void>;
  cancelEvent: (eventId: number) => Promise<void>;
}

export const useEvents = (selectedDate?: string): UseEventsResult => {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const startTime = getStartTimeForEvents(selectedDate);
      const fetchedEvents = await eventService.getAllEvents(startTime);
      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error loading events:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const cancelEvent = async (eventId: number) => {
    if (window.confirm('Are you sure you want to void all markets for this event?')) {
      try {
        await marketService.voidEventMarkets(eventId);
        await fetchEvents();
      } catch (err) {
        console.error('Failed to void event markets:', err);
        setError(err as Error);
      }
    }
  };

  return {
    events,
    isLoading,
    error,
    refreshEvents: fetchEvents,
    cancelEvent
  };
};
